import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useRequireAuth, useAuth } from '../../utils/auth'
import { platformApi } from '../../utils/api'
import { ThemeToggle } from '../../components/ThemeContext'
import { getPlatformConfig } from '../../utils/config'

export default function PlatformCreateTenant() {
  const navigate = useNavigate()
  const config = getPlatformConfig()
  const { loading, authorized } = useRequireAuth('platform')
  const { logout } = useAuth()
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    contactEmail: '',
    plan: 'free',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    primaryColor: '#0ea5e9',
  })
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Auto-generate slug from name
      ...(name === 'name' ? { slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') } : {}),
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    
    try {
      await platformApi.createTenant(formData)
      navigate('/platform/tenants')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    )
  }

  if (!authorized) {
    navigate('/platform')
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l7-4 7 4v14" />
              </svg>
            </div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Platform Admin</h1>
          </div>
          
          <nav className="flex items-center gap-4">
            <Link to="/platform/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition">Dashboard</Link>
            <Link to="/platform/tenants" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition">Tenants</Link>
            <ThemeToggle />
            <button onClick={logout} className="btn-ghost text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
              Sign Out
            </button>
          </nav>
        </div>
      </header>
      
      {/* Content */}
      <main className="max-w-2xl mx-auto px-6 py-8">
        <div className="mb-8">
          <Link to="/platform/tenants" className="text-brand-500 hover:text-brand-400 text-sm mb-2 inline-block">
            ← Back to Tenants
          </Link>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create Tenant</h2>
          <p className="text-gray-500 dark:text-gray-400">Set up a new organization on the platform</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {error && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
          
          {/* Organization Info */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Organization</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="label">Organization Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className="input"
                  placeholder="Kids Sports Club"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="slug" className="label">URL Slug</label>
                <div className="flex items-center">
                  <span className="px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-r-0 border-gray-300 dark:border-gray-700 rounded-l-lg text-gray-500 dark:text-gray-400 text-sm whitespace-nowrap">
                    {config.platformDomain}/t/
                  </span>
                  <input
                    id="slug"
                    name="slug"
                    type="text"
                    value={formData.slug}
                    onChange={handleChange}
                    className="input rounded-l-none"
                    placeholder="kids-sports-club"
                    pattern="[a-z0-9-]+"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="contactEmail" className="label">Contact Email</label>
                <input
                  id="contactEmail"
                  name="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  className="input"
                  placeholder="hello@club.com"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="plan" className="label">Plan</label>
                  <select
                    id="plan"
                    name="plan"
                    value={formData.plan}
                    onChange={handleChange}
                    className="input"
                  >
                    <option value="free">Free</option>
                    <option value="starter">Starter ($29/mo)</option>
                    <option value="pro">Pro ($99/mo)</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="primaryColor" className="label">Brand Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      id="primaryColor"
                      name="primaryColor"
                      value={formData.primaryColor}
                      onChange={handleChange}
                      className="w-12 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-700"
                    />
                    <input
                      type="text"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className="input flex-1"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Admin Account */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Admin Account</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Create the first admin account for this organization</p>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="adminName" className="label">Admin Name</label>
                <input
                  id="adminName"
                  name="adminName"
                  type="text"
                  value={formData.adminName}
                  onChange={handleChange}
                  className="input"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label htmlFor="adminEmail" className="label">Admin Email</label>
                <input
                  id="adminEmail"
                  name="adminEmail"
                  type="email"
                  value={formData.adminEmail}
                  onChange={handleChange}
                  className="input"
                  placeholder="john@club.com"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="adminPassword" className="label">Admin Password</label>
                <input
                  id="adminPassword"
                  name="adminPassword"
                  type="password"
                  value={formData.adminPassword}
                  onChange={handleChange}
                  className="input"
                  placeholder="••••••••"
                  minLength={8}
                  required
                />
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Link to="/platform/tenants" className="btn-secondary">
              Cancel
            </Link>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? 'Creating...' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </main>
    </div>
  )
}
